<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { NOTIFICATION_TYPES } from './constants';

export default {
  components: {
    TableHeaderCell,
    ToggleSwitch,
    CheckBox,
    Checkbox,
    NextButton,
    TagMultiSelectComboBox,
  },
  data() {
    return {
      selectedEmailFlags: [],
      selectedPushFlags: [],
      selectedLabelIds: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
      isBulkUpdating: false,
      notificationTypes: NOTIFICATION_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      labelIds: 'userNotificationSettings/getSelectedLabelIds',
      labels: 'labels/getLabels',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    filteredNotificationTypes() {
      return this.notificationTypes.filter(notification =>
        this.isSLAEnabled
          ? true
          : ![
              'sla_missed_first_response',
              'sla_missed_next_response',
              'sla_missed_resolution',
            ].includes(notification.value)
      );
    },
    bulkColumns() {
      return ['email', 'push'].map(type => {
        const visible = this.visibleFlags(type);
        const selected = this.flagsFor(type);
        const count = visible.filter(flag => selected.includes(flag)).length;
        return {
          type,
          allSelected: visible.length > 0 && count === visible.length,
          noneSelected: count === 0,
          partial: count > 0 && count < visible.length,
        };
      });
    },
    bulkColumnByType() {
      return Object.fromEntries(
        this.bulkColumns.map(column => [column.type, column])
      );
    },
    labelOptions() {
      return this.labels.map(label => ({
        value: Number(label.id),
        label: label.title,
      }));
    },
  },
  watch: {
    emailFlags(value) {
      this.selectedEmailFlags = value;
    },
    pushFlags(value) {
      this.selectedPushFlags = value;
    },
    labelIds(value) {
      this.selectedLabelIds = value.map(Number);
    },
  },
  mounted() {
    if (hasPushPermissions()) {
      this.getPushSubscription();
    }
    this.$store.dispatch('userNotificationSettings/get');
    this.$store.dispatch('labels/get');
  },
  methods: {
    flagsFor(type) {
      return type === 'email'
        ? this.selectedEmailFlags
        : this.selectedPushFlags;
    },
    // Only the notification types shown to this user are changed in bulk;
    // hidden flags (e.g. SLA without the feature) are preserved as they are.
    visibleFlags(type) {
      return this.filteredNotificationTypes.map(
        notification => `${type}_${notification.value}`
      );
    },
    bulkAriaLabel(type, action) {
      const key = `${type === 'email' ? 'EMAIL' : 'PUSH'}_${action}`;
      return this.$t(`PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.${key}`);
    },
    toggleColumn(type) {
      this.setAllFlags(type, !this.bulkColumnByType[type].allSelected);
    },
    async setAllFlags(type, enabled) {
      if (this.isBulkUpdating) return;
      const key = type === 'email' ? 'selectedEmailFlags' : 'selectedPushFlags';
      const previous = [...this[key]];
      const visible = this.visibleFlags(type);
      const next = enabled
        ? [...new Set([...previous, ...visible])]
        : previous.filter(flag => !visible.includes(flag));
      if (
        next.length === previous.length &&
        next.every(flag => previous.includes(flag))
      ) {
        return;
      }

      this[key] = next;
      this.isBulkUpdating = true;
      try {
        await this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
          selectedLabelIds: this.selectedLabelIds,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        this[key] = previous;
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      } finally {
        this.isBulkUpdating = false;
      }
    },
    checkFlagStatus(type, flagType) {
      const selectedFlags =
        type === 'email' ? this.selectedEmailFlags : this.selectedPushFlags;
      return selectedFlags.includes(`${type}_${flagType}`);
    },
    onRegistrationSuccess() {
      this.hasEnabledPushPermissions = true;
    },
    onRequestPermissions(value) {
      if (value) {
        // Enable / re-enable push notifications
        requestPushPermissions({
          onSuccess: this.onRegistrationSuccess,
        });
      } else {
        // Disable push notifications
        this.disablePushPermissions();
      }
    },
    disablePushPermissions() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (subscription) {
              return subscription.unsubscribe();
            }
            return null;
          })
          .finally(() => {
            this.hasEnabledPushPermissions = false;
          })
          .catch(() => {
            // error
          })
      );
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (!subscription) {
              this.hasEnabledPushPermissions = false;
            } else {
              this.hasEnabledPushPermissions = true;
            }
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    async updateNotificationSettings() {
      try {
        await this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
          selectedLabelIds: this.selectedLabelIds,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    handleInput(type, id) {
      if (type === 'email') {
        this.handleEmailInput(id);
      } else {
        this.handlePushInput(id);
      }
    },
    handleEmailInput(id) {
      this.selectedEmailFlags = this.toggleInput(this.selectedEmailFlags, id);
      this.updateNotificationSettings();
    },
    handlePushInput(id) {
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      this.updateNotificationSettings();
    },
    handleLabelInput(labelIds) {
      this.selectedLabelIds = labelIds.map(Number);
      this.updateNotificationSettings();
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <!-- Layout for desktop devices -->
    <div class="hidden sm:block">
      <div
        class="grid content-center min-h-12 grid-cols-12 gap-4 py-2 rounded-t-xl"
      >
        <TableHeaderCell
          :span="7"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          v-for="column in bulkColumns"
          :key="column.type"
          :span="column.type === 'push' ? 3 : 2"
          :label="
            $t(
              column.type === 'email'
                ? 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL'
                : 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH'
            )
          "
        >
          <div class="flex flex-col items-start gap-1">
            <label
              class="flex items-center gap-2 text-heading-3 normal-case text-n-slate-12 whitespace-nowrap"
            >
              <Checkbox
                :model-value="column.allSelected"
                :indeterminate="column.partial"
                :disabled="isBulkUpdating"
                :data-test-id="`bulk-toggle-${column.type}`"
                @change="toggleColumn(column.type)"
              />
              {{
                $t(
                  column.type === 'email'
                    ? 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL'
                    : 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH'
                ).trim()
              }}
            </label>
            <div class="flex flex-wrap items-center gap-x-2 normal-case">
              <NextButton
                xs
                link
                :label="
                  $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.SELECT_ALL')
                "
                :aria-label="bulkAriaLabel(column.type, 'SELECT_ALL')"
                :disabled="isBulkUpdating || column.allSelected"
                :data-test-id="`bulk-select-${column.type}`"
                @click="setAllFlags(column.type, true)"
              />
              <NextButton
                xs
                link
                slate
                :label="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.CLEAR')"
                :aria-label="bulkAriaLabel(column.type, 'CLEAR')"
                :disabled="isBulkUpdating || column.noneSelected"
                :data-test-id="`bulk-clear-${column.type}`"
                @click="setAllFlags(column.type, false)"
              />
            </div>
          </div>
        </TableHeaderCell>
      </div>
      <div
        v-for="(notification, index) in filteredNotificationTypes"
        :key="index"
      >
        <div
          class="grid items-center content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
        >
          <div
            class="flex flex-row items-start gap-2 col-span-7 px-0 py-2 text-sm tracking-[0.5] rtl:text-right"
          >
            <span class="text-body-main text-n-slate-12">
              {{ $t(notification.label) }}
            </span>
          </div>
          <div
            v-for="(type, typeIndex) in ['email', 'push']"
            :key="typeIndex"
            class="flex items-start gap-2 px-0 text-sm tracking-[0.5] text-left rtl:text-right"
            :class="`col-span-${type === 'push' ? 3 : 2}`"
          >
            <CheckBox
              :value="`${type}_${notification.value}`"
              :is-checked="
                checkFlagStatus(type, notification.value, selectedPushFlags)
              "
              @update="id => handleInput(type, id)"
            />
          </div>
        </div>
      </div>
    </div>
    <!--  Layout for mobile devices -->
    <div class="flex flex-col gap-6 sm:hidden">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
        <div class="flex items-center gap-2">
          <NextButton
            xs
            link
            :label="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.SELECT_ALL')"
            :aria-label="bulkAriaLabel('email', 'SELECT_ALL')"
            :disabled="isBulkUpdating || bulkColumnByType.email.allSelected"
            @click="setAllFlags('email', true)"
          />
          <NextButton
            xs
            link
            slate
            :label="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.CLEAR')"
            :aria-label="bulkAriaLabel('email', 'CLEAR')"
            :disabled="isBulkUpdating || bulkColumnByType.email.noneSelected"
            @click="setAllFlags('email', false)"
          />
        </div>
      </div>
      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`email_${notification.value}`"
            :value="`email_${notification.value}`"
            :is-checked="checkFlagStatus('email', notification.value)"
            @update="handleEmailInput"
          />
          <span class="text-body-main text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
        <div class="flex items-center gap-2">
          <NextButton
            xs
            link
            :label="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.SELECT_ALL')"
            :aria-label="bulkAriaLabel('push', 'SELECT_ALL')"
            :disabled="isBulkUpdating || bulkColumnByType.push.allSelected"
            @click="setAllFlags('push', true)"
          />
          <NextButton
            xs
            link
            slate
            :label="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BULK.CLEAR')"
            :aria-label="bulkAriaLabel('push', 'CLEAR')"
            :disabled="isBulkUpdating || bulkColumnByType.push.noneSelected"
            @click="setAllFlags('push', false)"
          />
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`push_${notification.value}`"
            :value="`push_${notification.value}`"
            :is-checked="checkFlagStatus('push', notification.value)"
            @update="handlePushInput"
          />
          <span class="text-body-main text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>
    </div>

    <div class="grid gap-2 border-t border-n-weak pt-6">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.LABEL_ALERTS.TITLE') }}
      </span>
      <p class="mb-2 text-body-main text-n-slate-11">
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.LABEL_ALERTS.DESCRIPTION') }}
      </p>
      <TagMultiSelectComboBox
        :model-value="selectedLabelIds"
        :options="labelOptions"
        :placeholder="
          $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.LABEL_ALERTS.PLACEHOLDER')
        "
        :search-placeholder="
          $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.LABEL_ALERTS.SEARCH')
        "
        :empty-state="
          $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.LABEL_ALERTS.EMPTY')
        "
        @update:model-value="handleLabelInput"
      />
      <p class="mb-0 text-xs text-n-slate-10">
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.LABEL_ALERTS.HINT') }}
      </p>
    </div>

    <div
      class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex flex-row items-center gap-2">
        <fluent-icon
          icon="alert"
          class="flex-shrink-0 text-n-slate-12"
          size="18"
        />
        <span class="text-body-main text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <ToggleSwitch
        v-model="hasEnabledPushPermissions"
        @change="onRequestPermissions"
      />
    </div>
  </div>
</template>
